import React, { useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import * as am5 from '@amcharts/amcharts5';
import * as am5hierarchy from '@amcharts/amcharts5/hierarchy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import './index.css';

const CurriculumLevels = ({ chartID, dataSet }) => {
  useLayoutEffect(() => {
    // Check if dataSet is empty.
    // Get data.
    const data = [
      {
        parent: 'Data Curriculum',
        type: 'Main',
        children: [...dataSet.datasets]
      }
    ];

    const arrayOfTypes = dataSet.types;

    // Set root instance.
    const root = am5.Root.new(chartID);

    // Set themes.
    root.setThemes([am5themes_Animated.new(root)]);

    // Remove logo.
    root._logo.dispose(); // Create wrapper container

    // Add container.
    let container = root.container.children.push(
      am5.Container.new(root, {
        width: am5.percent(100),
        height: am5.percent(100),
        layout: root.verticalLayout
      })
    );

    // Create series.
    let series = container.children.push(
      am5hierarchy.Partition.new(root, {
        singleBranchOnly: false,
        calculateAggregates: false,
        orientation: 'horizontal',
        topDepth: 1,
        downDepth: 2,
        upDepth: 0,
        initialDepth: 3,
        valueField: 'value',
        categoryField: 'parent',
        childDataField: 'children'
        // legendLabelText: '----',
        // legendValueText: "{avg.formatNumber('#.#')}"
      })
    );

    // Rounded rectangles.
    series.rectangles.template.setAll({
      fillOpacity: 1,
      cornerRadiusTL: 4,
      cornerRadiusTR: 4,
      cornerRadiusBL: 4,
      cornerRadiusBR: 4
    });

    // Set Generals colors of ana-chart.
    series.rectangles.template.adapters.add('fill', (fill, target) => {
      const dataItem = target.dataItem;
      if (!!dataItem) {
        fill = dataItem.dataContext.hasOwnProperty('fill') ? am5.color(dataItem.dataContext.fill) : fill;
        if (dataItem.dataContext.hasOwnProperty('type') && dataItem.dataContext.type.includes('level')) {
          target.set('fillOpacity', 1);
          return fill;
        }
        let getIndex = arrayOfTypes.indexOf(dataItem.dataContext.type);
        let opacity = 1;
        if (getIndex !== -1) {
          getIndex += 1;
          opacity = opacity - getIndex / 20;
          target.set('fillOpacity', opacity);
        }
      }
      return fill;
    });

    // Set Tooltip.
    series.nodes.template.setup = (target) => {
      target.events.on('dataitemchanged', (targetRender) => {
        target.set('tooltipText', null);
        const dataItem = targetRender.target.dataItem;
        if (
          !!dataItem &&
          dataItem.dataContext.hasOwnProperty('type') &&
          dataItem.dataContext.type.includes('Levels') &&
          dataItem.dataContext.hasOwnProperty('log') &&
          dataItem.dataContext.log !== null
        ) {
          let text = '';
          if (dataItem.dataContext.hasOwnProperty('log')) {
            text += `[bold  textAlign: right]{log.markingDate.formatDate('dd-MMM-yyyy')} `;
            text += `\n[bold]Level: [/] {log.levelName} [/]`;
            text += `\n[bold]Marking type: [/] [fontStyle: italic]{log.markingName} `;
            text += `\n[bold]Marking by: [/] [fontStyle: normal]{log.markingBy}`;
            if (
              dataItem.dataContext.log.hasOwnProperty('details') &&
              dataItem.dataContext.log.details.length > 0
            ) {
              text += `\n[bold]Detail:[/]`;
              dataItem.dataContext.log.details.forEach((item) => {
                text += `\n-${item}`;
              });
            }
          }
          target.set('tooltipText', text);
          
        }
      });
    };

    series.labels.template.setAll({
      oversizedBehavior: 'wrap',
      maxLines: 4,
      overflow: 'hide',
      wrap: true,
      // minHeight: 12,
      // mixHeight: 50,
      // height: 70,
      // minScale: 0.5,
      breakWords: true,
      fontSize: 12,
      paddingLeft: 10,
      paddingRight: 10,
      fill: '#FFFFFF',
      textAlign: 'justify',
      textBaseline: 'middle',
      textOverflow: 'ellipsis',
      textWrap: 'ellipsis',
      textOverflowWidth: 100,
      textOverflowHeight: 50,
      x: am5.percent(0),
      centerX: am5.percent(0),
      text: ''

    });

    

    series.labels.template.adapters.add('text', (text, target) => {
      const dataItem = target.dataItem;
      if (!!dataItem) {
        text = `[bold fontSize: 10px fontStyle: italic]{type}\n[fontSize: 14px]{category}[/]`;
        if (dataItem.dataContext.type === 'Levels') {
          text = `[bold]{levelName} [/] [fontSize: 10px]{category}[/]`;
        } else {
          text += `\n[bold]{levelName}[/]`;
        }

        if (dataItem.dataContext.hasOwnProperty('type') && dataItem.dataContext.type.includes('Levels')) {
          text = text.replace("type", "levelName");
          text += `\n[#FFF bold fontSize: 10px]{markingName}[/].`;
          return text;
        };
        return text;
      }
    });
    

    let nav = container.children.unshift(
      am5hierarchy.BreadcrumbBar.new(root, {
        series: series
      })
    );

    nav.labels.template.setAll({
      fontSize: 14,
      fill: am5.color(0x888888)
    });

    nav.labels.template.adapters.add('text', function (text, target) {
      const maxLength = 10;
      const dataItem = target?.dataItem;
      if (!!dataItem) {
        if (dataItem?.dataContext?.parent?.length > maxLength) {
          text = `${dataItem.dataContext.parent.substring(0, maxLength)}...`;
        }
      }
      return text;
    });

    series.data.setAll(data);
    series.set('selectedDataItem', series.dataItems[0]);

    // Make stuff animate on load.
    series.appear(1000, 100);

    // Return before dom render.
    return () => root.dispose();
  }, [chartID, dataSet]);

  return <div id={chartID} className='col-chart-cur'></div>;
};
// Add PropTypes validation.
CurriculumLevels.propTypes = {
  chartID: PropTypes.string.isRequired,
  dataSet: PropTypes.object.isRequired
};

export default React.memo(CurriculumLevels);
